import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { Form } from "antd";
import { useMessageApi } from "../../../../shared/providers/MessageProvider";
import { hasPermission } from "../../../../utils/roleHelpers";
import { DEBOUNCE_CONFIG } from "../../../../utils/constants";
import RateReviewTable from "../Table/RateReviewTable";
import UpdateLeadModal from "../Modals/UpdateLeadModal/UpdateLeadModal";
import DateRangePicker from "../../../../shared/components/DateRangePicker/DateRangePicker";
import { RATE_REVIEW_CONSTANTS } from "../../constants/rateReviewConstants";
import { fetchRateReviewLeads, searchRateReviewLeadsCombined, updateRateReviewLead, processLeadUpdateValues } from "../../services/rateReviewApiService";
import { getInitialFilterState, getInitialFilterLoadingStates, clearFilter as clearFilterHelper } from "../../utils/filterStateHelpers";
import { mapLeadToFormValues } from "../../utils/leadFormatters";
import { sortLeadsByDateCreated, getNextSortDirection } from "../../utils/sortingHelpers";
import { getStatusClass } from "../../utils/rateReviewHelpers";
import useBodyScrollLock from "../../../../hooks/useBodyScrollLock";
import "./RateReviewDashboard.scss";

/**
 * Rate Review Dashboard Component
 */
const RateReviewDashboard = ({ userInfo = null }) => {
  const message = useMessageApi();
  
  const canEdit = useMemo(
    () => hasPermission(userInfo, 'rate_review_portal', 'edit'),
    [userInfo]
  );

  const [leads, setLeads] = useState([]);
  const [selectedLead, setSelectedLead] = useState(null);
  const [isTableLoading, setIsTableLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [isUpdateModalVisible, setIsUpdateModalVisible] = useState(false);
  const [isUpdatingLead, setIsUpdatingLead] = useState(false);
  const [saveTick, setSaveTick] = useState(0);
  const [updateForm] = Form.useForm();
  const [filters, setFilters] = useState(getInitialFilterState());
  const [filterLoadingStates, setFilterLoadingStates] = useState(getInitialFilterLoadingStates());
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [sortColumn, setSortColumn] = useState(null);
  const [sortDirection, setSortDirection] = useState(null);
  const [isStatusDropdownOpen, setIsStatusDropdownOpen] = useState(false);
  const [statusSearchTerm, setStatusSearchTerm] = useState("");
  const [isDateSubmittedOpen, setIsDateSubmittedOpen] = useState(false);

  const hasFetchedInitialDataRef = useRef(false);
  const isInitialLoadInProgressRef = useRef(false);
  const isComponentMountedRef = useRef(true);
  const isFetchingDataRef = useRef(false);
  const currentFiltersRef = useRef(filters);
  const filterDebounceTimerRef = useRef(null);
  const lastChangedFilterNameRef = useRef(null);

  useEffect(() => {
    currentFiltersRef.current = filters;
  }, [filters]);

  useEffect(() => {
    isComponentMountedRef.current = true;
    return () => {
      isComponentMountedRef.current = false;
      if (filterDebounceTimerRef.current) {
        clearTimeout(filterDebounceTimerRef.current);
      }
    };
  }, []);

  /**
   * Fetches leads from API with applied filters and pagination
   */
  const fetchLeadsFromApi = useCallback(
    async (pageNumber = 1, filterParams = {}, shouldShowLoader = true, isAppendingData = false, isInitialDataLoad = false) => {
      if (!isComponentMountedRef.current || (!isAppendingData && isFetchingDataRef.current)) return;

      isFetchingDataRef.current = true;
      
      if (shouldShowLoader) {
        isAppendingData ? setIsLoadingMore(true) : setIsTableLoading(true);
      }

      try {
        const { leads: fetchedLeads, hasMore: hasMoreData } = await fetchRateReviewLeads({
          page: pageNumber,
          filters: filterParams,
        });

        if (!isComponentMountedRef.current) return;

        setLeads((prevLeads) => (isAppendingData ? [...prevLeads, ...fetchedLeads] : fetchedLeads));
        setHasMore(hasMoreData);
      } catch (error) {
        if (isComponentMountedRef.current) {
          const errorMessage =
            error.response?.data?.error ||
            error.message ||
            "Failed to fetch leads";
          message.error(errorMessage, 3);
        }
      } finally {
        isFetchingDataRef.current = false;
        
        if (shouldShowLoader && isComponentMountedRef.current) {
          isAppendingData ? setIsLoadingMore(false) : setIsTableLoading(false);
        }
        
        if (isInitialDataLoad) {
          setTimeout(() => {
            isInitialLoadInProgressRef.current = false;
          }, 0);
        }
      }
    },
    [message]
  );

  useEffect(() => {
    if (!hasFetchedInitialDataRef.current && isComponentMountedRef.current) {
      hasFetchedInitialDataRef.current = true;
      isInitialLoadInProgressRef.current = true;
      setFilterLoadingStates(getInitialFilterLoadingStates());
      fetchLeadsFromApi(1, getInitialFilterState(), true, false, true);
    }
  }, [fetchLeadsFromApi]);

  /**
   * Builds search filters from current filter state for API calls
   * 
   * Transforms UI filter state into API-ready format:
   * - Only includes non-empty filter values (removes empty strings, nulls, empty arrays)
   * - Trims whitespace from text inputs
   * - Handles both preset and custom date ranges
   * - Supports multiple date range formats (start/end vs startDate/endDate)
   * 
   * @param {Object} inputFilters - Filter state to build from (defaults to current filters)
   * @returns {Object} Filter object ready for API call (only contains active filters)
   */
  const buildApiSearchFilters = useCallback((inputFilters = filters) => {
    const apiFilters = {};

    if (inputFilters.searchValue?.trim()) {
      apiFilters.searchValue = inputFilters.searchValue.trim();
    }

    if (inputFilters.phone?.trim()) {
      apiFilters.phone = inputFilters.phone.trim();
    }

    if (inputFilters.gatewayId?.trim()) {
      apiFilters.gatewayId = inputFilters.gatewayId.trim();
    }

    if (inputFilters.status && inputFilters.status.length > 0) {
      apiFilters.status = inputFilters.status;
    }

    if (inputFilters.dateSubmitted) {
      if (inputFilters.dateSubmitted.preset) {
        apiFilters.dateSubmitted = {
          rangeType: inputFilters.dateSubmitted.preset,
          preset: inputFilters.dateSubmitted.preset,
          label: inputFilters.dateSubmitted.label,
        };
      } else {
        const startDate = inputFilters.dateSubmitted.start || inputFilters.dateSubmitted.startDate;
        const endDate = inputFilters.dateSubmitted.end || inputFilters.dateSubmitted.endDate;
        
        if (startDate && endDate) {
          apiFilters.dateSubmitted = {
            startDate,
            endDate,
          };
        }
      }
    }

    return apiFilters;
  }, [filters]);

  /**
   * Sets loading state for the specific filter that was changed
   * 
   * This provides better UX by showing a loading spinner only on the filter input
   * that was modified, rather than showing loaders on all active filters.
   * Only shows loader if the filter actually has a value (not empty).
   * 
   * @param {string} filterName - Name of the filter that was changed
   * @param {Object} apiFilters - Built API filters to check if filter has a value
   */
  const setFilterLoadingState = useCallback((filterName, apiFilters) => {
    const filterHasValue = 
      (filterName === 'searchValue' && apiFilters.searchValue) ||
      (filterName === 'phone' && apiFilters.phone) ||
      (filterName === 'gatewayId' && apiFilters.gatewayId) ||
      (filterName === 'status' && apiFilters.status && apiFilters.status.length > 0) ||
      (filterName === 'dateSubmitted' && apiFilters.dateSubmitted);

    if (filterHasValue) {
      setFilterLoadingStates((prev) => ({ ...prev, [filterName]: true }));
    }
  }, []);

  /**
   * Checks if any filter is active and triggers search with combined filters
   * 
   * This is the core search function that:
   * - Debounces filter changes to prevent excessive API calls
   * - Uses combined search API when filters are active (faster, no pagination)
   * - Uses regular paginated API when no filters are active
   * - Shows loading indicators appropriately
   * - Ensures all filters work together in combination (matching MainView behavior)
   * 
   * @param {Object} inputFilters - Filter state to check (defaults to current filters)
   */
  const triggerFilteredSearch = useCallback((inputFilters = filters) => {
    if (!hasFetchedInitialDataRef.current) return;

    const apiFilters = buildApiSearchFilters(inputFilters);

    const hasActiveFilters = Boolean(
      apiFilters.searchValue ||
      apiFilters.phone ||
      apiFilters.gatewayId ||
      (apiFilters.status && apiFilters.status.length > 0) ||
      apiFilters.dateSubmitted
    );

    if (filterDebounceTimerRef.current) {
      clearTimeout(filterDebounceTimerRef.current);
    }

    const changedFilterName = lastChangedFilterNameRef.current;
    if (changedFilterName) {
      setFilterLoadingState(changedFilterName, apiFilters);
    }
    
    setIsTableLoading(true);

    const debounceDelay = DEBOUNCE_CONFIG.COMBINED_SEARCH;

    filterDebounceTimerRef.current = setTimeout(async () => {
      setCurrentPage(1);
      
      try {
        if (hasActiveFilters) {
          const result = await searchRateReviewLeadsCombined(apiFilters);
          
          if (!isComponentMountedRef.current) return;
          
          setLeads(result.leads || []);
          setHasMore(false);
          
          if (isComponentMountedRef.current) {
            setIsTableLoading(false);
          }
        } else {
          await fetchLeadsFromApi(1, apiFilters, true, false);
        }
      } catch (error) {
        if (isComponentMountedRef.current) {
          const errorMessage =
            error.response?.data?.error ||
            error.message ||
            "Failed to search leads";
          message.error(errorMessage, 3);
          setIsTableLoading(false);
        }
      } finally {
        if (isComponentMountedRef.current) {
          const changedFilterName = lastChangedFilterNameRef.current;
          if (changedFilterName) {
            setFilterLoadingStates((prev) => ({
              ...prev,
              [changedFilterName]: false,
            }));
            lastChangedFilterNameRef.current = null;
          }
        }
      }
    }, debounceDelay);
  }, [filters, buildApiSearchFilters, fetchLeadsFromApi, message, setFilterLoadingState]);

  /**
   * Handles filter value changes from user input
   * 
   * Updates filter state and tracks which filter was changed.
   * The filter effect will automatically trigger a search when filters change.
   * 
   * @param {string} filterName - Name of the filter being changed (e.g., 'searchValue', 'phone')
   * @param {any} newValue - New filter value from the input field
   */
  const onFilterValueChange = useCallback((filterName, newValue) => {
    lastChangedFilterNameRef.current = filterName;
    
    setFilters((prevFilters) => {
      const updatedFilters = { ...prevFilters, [filterName]: newValue };
      currentFiltersRef.current = updatedFilters;
      return updatedFilters;
    });
  }, []);

  /**
   * Clears a specific filter back to its default/empty state
   * 
   * Uses helper function to properly reset the filter value based on filter type.
   * Also tracks which filter was cleared for loading state management.
   * 
   * @param {string} filterName - Name of the filter to clear (e.g., 'searchValue', 'status')
   */
  const onFilterClear = useCallback(
    (filterName) => {
      lastChangedFilterNameRef.current = filterName;
      
      setFilters((prevFilters) => {
        const clearedFilters = clearFilterHelper(filterName, prevFilters);
        currentFiltersRef.current = clearedFilters;
        return clearedFilters;
      });
    },
    []
  );

  useEffect(() => {
    if (!hasFetchedInitialDataRef.current) return;
    if (isInitialLoadInProgressRef.current) return;

    const dateSubmitted = filters.dateSubmitted;
    const isIncompleteDateRange = dateSubmitted?.start && !dateSubmitted?.end && !dateSubmitted?.preset;

    if (isIncompleteDateRange) {
      return;
    }

    triggerFilteredSearch(filters);
  }, [filters, triggerFilteredSearch]);

  /**
   * Loads more data for infinite scroll pagination
   * 
   * Appends the next page of results to existing leads when user scrolls near bottom.
   * Only loads if there are more leads available and no load is already in progress.
   * 
   * This function is called automatically by the scroll handler when user reaches
   * the scroll threshold near the bottom of the page.
   */
  const loadMoreLeads = useCallback(() => {
    if (hasMore && !isLoadingMore && !isTableLoading) {
      const nextPage = currentPage + 1;
      setCurrentPage(nextPage);
      fetchLeadsFromApi(nextPage, filters, true, true);
    }
  }, [hasMore, isLoadingMore, isTableLoading, currentPage, filters, fetchLeadsFromApi]);

  useEffect(() => {
    const handleWindowScroll = () => {
      const { pageYOffset, innerHeight } = window;
      const { scrollHeight } = document.documentElement;

      if (pageYOffset + innerHeight >= scrollHeight - RATE_REVIEW_CONSTANTS.SCROLL_LOAD_THRESHOLD) {
        loadMoreLeads();
      }
    };

    window.addEventListener("scroll", handleWindowScroll);
    return () => window.removeEventListener("scroll", handleWindowScroll);
  }, [loadMoreLeads]);

  useBodyScrollLock(isUpdateModalVisible);

  /**
   * Handles date range preset selection (Today, Yesterday, Week To Date, etc.)
   * 
   * When a preset is selected, we store only the preset key and label.
   * Start/end dates are set to null because presets are calculated dynamically by the API.
   * This matches MainView logic where preset selection shows the label, not actual dates.
   * 
   * @param {string} presetKey - Preset key from constants (today, yesterday, week_to_date, etc.)
   */
  const onDatePresetSelect = useCallback(
    (presetKey) => {
      lastChangedFilterNameRef.current = 'dateSubmitted';
      
      const presetLabel = RATE_REVIEW_CONSTANTS.DATE_PRESET_LABELS[presetKey] || null;
      
      const dateSubmitted = {
        start: null,
        end: null,
        preset: presetKey,
        label: presetLabel,
      };
      
      setFilters((prevFilters) => {
        const updatedFilters = { ...prevFilters, dateSubmitted };
        currentFiltersRef.current = updatedFilters;
        return updatedFilters;
      });
    },
    []
  );

  /**
   * Handles lead row click - opens edit modal
   * 
   * When user clicks on a lead row in the table:
   * 1. Maps lead data to form values format
   * 2. Sets selected lead and populates form
   * 3. Opens the update modal (even without edit permission - modal will handle disabling edits)
   * 
   * @param {Object} lead - Lead object to edit (from table row)
   */
  const onLeadRowClick = useCallback(
    (lead) => {
      const formValues = mapLeadToFormValues(lead);
      setSelectedLead({ ...lead, ...formValues });
      updateForm.setFieldsValue(formValues);
      setIsUpdateModalVisible(true);
    },
    [updateForm]
  );

  /**
   * Updates transformed display fields in lead object
   * 
   * When a lead is updated, we need to update both:
   * 1. Raw Zoho fields (Company, First_Name, Last_Name, etc.)
   * 2. Transformed display fields (businessName, contactName, etc.)
   * 
   * The table displays transformed fields, so we need to keep them in sync
   * with the raw Zoho fields when updates occur.
   * 
   * @param {Object} lead - Lead object to update
   * @param {Object} updates - Update values from API (raw Zoho field names)
   * @returns {Object} Updated lead object with both raw and transformed fields
   */
  const updateLeadDisplayFields = useCallback((lead, updates) => {
    if (!lead || !updates) return lead;
    
    const updatedLead = { ...lead };
    
    // Apply all updates first
    Object.keys(updates).forEach((key) => {
      updatedLead[key] = updates[key];
    });
    
    // Update display fields based on raw field updates
    if (updates.Company !== undefined) {
      updatedLead.businessName = updates.Company;
    }
    
    if (updates.First_Name !== undefined || updates.Last_Name !== undefined) {
      const firstName = updates.First_Name ?? updatedLead.First_Name ?? "";
      const lastName = updates.Last_Name ?? updatedLead.Last_Name ?? "";
      updatedLead.contactName = `${firstName} ${lastName}`.trim() || "N/A";
    }
    
    if (updates.Email !== undefined) {
      updatedLead.email = updates.Email;
    }
    
    if (updates.Phone !== undefined) {
      updatedLead.phone = updates.Phone;
    }
    
    if (updates.Gateway_ID !== undefined) {
      updatedLead.gatewayId = updates.Gateway_ID;
    }
    
    if (updates.Lead_Status !== undefined) {
      updatedLead.status = updates.Lead_Status;
      updatedLead.Lead_Status = updates.Lead_Status;
      updatedLead.statusClass = getStatusClass(updates.Lead_Status);
    }
    
    if (updates.Savings !== undefined) {
      updatedLead.Savings = updates.Savings;
      updatedLead.savings = updates.Savings;
    }
    
    return updatedLead;
  }, []);

  /**
   * Handles lead update submission
   * 
   * This function processes form values and updates the lead via API.
   * After successful update, it:
   * - Updates the lead in the table immediately (optimistic update)
   * - Updates the selected lead in the modal
   * - Handles different update types (status-only, savings, regular)
   * - Conditionally shows success messages based on update type
   * 
   * @param {Object} formValues - Form values from update modal
   * @param {Object} options - Options object with:
   *   - keepOpen: boolean - Whether to keep modal open after update
   *   - source: string - Source of update (e.g., "file-reviewed-status-update", "savings")
   */
  const onLeadUpdate = useCallback(
    async (formValues, options = {}) => {
      if (!selectedLead) return;

      setIsUpdatingLead(true);
      try {
        const updates = processLeadUpdateValues(formValues);

        if (Object.keys(updates).length === 0) {
          message.warning("No changes to update", 2);
          setIsUpdatingLead(false);
          return;
        }

        const response = await updateRateReviewLead({ leadId: selectedLead.id, updates });
        const actualUpdates = response?.data?.updatedFields || response?.updatedFields || updates;

        const isStatusOnlyUpdate = 
          options.source === "file-reviewed-status-update" ||
          (Object.keys(updates).length === 1 && updates.Lead_Status !== undefined);

        const isSavingsOnlyUpdate = options.source === "savings";

        if (!isStatusOnlyUpdate && !isSavingsOnlyUpdate) {
          message.success("Lead updated successfully!", 3);
        }
        
        // Update selectedLead (modal) and leads array (table) with actual updates from server
        const leadId = selectedLead.id || selectedLead.Id;
        if (!leadId) return;
        
        // Update selectedLead (modal) with actual updates
        setSelectedLead((prevLead) => {
          if (!prevLead) return prevLead;
          const updatedLead = updateLeadDisplayFields(prevLead, actualUpdates);
          return updatedLead;
        });
        
        // Update leads array (table) with actual updates
        setLeads((prevLeads) => {
          let updatedLead = null;
          const otherLeads = [];
          
          // Find and update the lead in one pass
          prevLeads.forEach((lead) => {
            const currentLeadId = lead.id || lead.Id;
            if (currentLeadId === leadId) {
              updatedLead = updateLeadDisplayFields(lead, actualUpdates);
            } else {
              otherLeads.push(lead);
            }
          });
          
          return updatedLead ? [updatedLead, ...otherLeads] : prevLeads;
        });
        
        setSaveTick((prev) => prev + 1);
        
        if (!options.keepOpen) {
          setIsUpdateModalVisible(false);
          updateForm.resetFields();
          setSelectedLead(null);
        }
      } catch (error) {
        const errorMessage =
          error.response?.data?.error ||
          error.response?.data?.message ||
          error.message ||
          "Failed to update lead";
        message.error(errorMessage, 3);
      } finally {
        setIsUpdatingLead(false);
      }
    },
    [selectedLead, updateForm, message, updateLeadDisplayFields]
  );

  /**
   * Closes the update modal and resets form
   * 
   * Called when user clicks cancel or closes the modal.
   * Cleans up modal state and form data.
   */
  const onUpdateModalClose = useCallback(() => {
    setIsUpdateModalVisible(false);
    updateForm.resetFields();
    setSelectedLead(null);
  }, [updateForm]);

  /**
   * Handles column sorting (currently only Date Submitted column)
   * 
   * Cycles through sort states: none -> ascending -> descending -> none
   * Each click on the sortable column header advances to the next state.
   * 
   * @param {number} columnIndex - Index of column to sort (0-based)
   */
  const onColumnSort = useCallback(
    (columnIndex) => {
      if (columnIndex === RATE_REVIEW_CONSTANTS.DATE_SUBMITTED_COLUMN_INDEX) {
        const nextSortDirection = getNextSortDirection(sortColumn, columnIndex, sortDirection);
        
        if (nextSortDirection === null) {
          setSortColumn(null);
          setSortDirection(null);
        } else {
          setSortColumn(columnIndex);
          setSortDirection(nextSortDirection);
        }
      }
    },
    [sortColumn, sortDirection]
  );

  /**
   * Sorts leads based on selected column and direction
   * 
   * Currently only supports sorting by Date Submitted column.
   * Returns original array if no sort is applied or if sorting by unsupported column.
   * 
   * Memoized to prevent unnecessary re-sorting on every render.
   * 
   * @returns {Array} Sorted array of leads or original array if no sort applied
   */
  const sortedLeads = useMemo(() => {
    if (!sortColumn || !sortDirection || sortColumn !== RATE_REVIEW_CONSTANTS.DATE_SUBMITTED_COLUMN_INDEX) {
      return leads;
    }
    return sortLeadsByDateCreated(leads, sortDirection);
  }, [leads, sortColumn, sortDirection]);

  const availableStatuses = RATE_REVIEW_CONSTANTS.AVAILABLE_STATUSES;

  /**
   * Filters available statuses based on search term
   * 
   * When user types in the status dropdown search box, this filters the list
   * of available statuses to show only those matching the search term.
   * Returns all statuses if search term is empty.
   * 
   * Memoized to prevent unnecessary filtering on every render.
   * 
   * @returns {Array} Filtered array of statuses matching search term
   */
  const filteredStatuses = useMemo(() => {
    if (!statusSearchTerm.trim()) {
      return availableStatuses;
    }
    return availableStatuses.filter((status) =>
      status.toLowerCase().includes(statusSearchTerm.toLowerCase())
    );
  }, [statusSearchTerm, availableStatuses]);

  /**
   * Toggles status dropdown open/closed state
   * 
   * When opening the status dropdown, automatically closes the date picker
   * to prevent both dropdowns from being open simultaneously (better UX).
   */
  const onStatusDropdownToggle = useCallback(() => {
    setIsStatusDropdownOpen((prevIsOpen) => {
      const newIsOpen = !prevIsOpen;
      if (newIsOpen) {
        setIsDateSubmittedOpen(false);
      }
      return newIsOpen;
    });
  }, []);

  /**
   * Handles status checkbox changes in the status filter dropdown
   * 
   * Adds or removes a status from the selected statuses array based on checkbox state.
   * When a status is checked, it's added to the filter. When unchecked, it's removed.
   * 
   * @param {string} statusName - Status name to add/remove (e.g., "New", "In Progress")
   * @param {boolean} isChecked - Whether the status checkbox is checked (true = add, false = remove)
   */
  const onStatusCheckboxChange = useCallback((statusName, isChecked) => {
    lastChangedFilterNameRef.current = 'status';
    
    setFilters((prevFilters) => {
      const currentStatuses = prevFilters.status || [];
      
      const updatedFilters = isChecked
        ? { ...prevFilters, status: [...currentStatuses, statusName] }
        : { ...prevFilters, status: currentStatuses.filter((s) => s !== statusName) };
      
      currentFiltersRef.current = updatedFilters;
      return updatedFilters;
    });
  }, []);

  /**
   * Removes a status from selected statuses
   * 
   * Convenience function for removing a status tag from the selected statuses.
   * This is called when user clicks the X button on a status tag.
   * 
   * @param {string} statusToRemove - Status name to remove from selection
   */
  const onStatusRemove = useCallback(
    (statusToRemove) => {
      onStatusCheckboxChange(statusToRemove, false);
    },
    [onStatusCheckboxChange]
  );

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isStatusDropdownOpen && !event.target.closest(".status-dropdown")) {
        setIsStatusDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isStatusDropdownOpen]);

  useEffect(() => {
    if (!isStatusDropdownOpen) {
      setStatusSearchTerm("");
    }
  }, [isStatusDropdownOpen]);

  const filterRow = useMemo(() => {
    return (
      <tr className="filter-row">
        <td colSpan={3} className="filter-cell">
          <div className="floating-input-group search-business-name">
            <input
              type="text"
              placeholder=" "
              value={filters.searchValue}
              onChange={(e) => onFilterValueChange("searchValue", e.target.value)}
              className={`floating-input ${filters.searchValue ? "has-value" : ""}`}
            />
            <label className="floating-label">{RATE_REVIEW_CONSTANTS.SEARCH_LABEL}</label>
            {filterLoadingStates.searchValue && (
              <div className="search-loading">
                <div className="simple-spinner"></div>
              </div>
            )}
            {filters.searchValue && !filterLoadingStates.searchValue && (
              <button
                type="button"
                className="clear-input-btn"
                onClick={() => onFilterClear("searchValue")}
                title="Clear Search"
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
            )}
          </div>
        </td>

        <td className="filter-cell">
          <div className="floating-input-group">
            <input
              type="text"
              placeholder=" "
              value={filters.phone}
              onChange={(e) => onFilterValueChange("phone", e.target.value)}
              className={`floating-input ${filters.phone ? "has-value" : ""}`}
            />
            <label className="floating-label">Phone</label>
            {filterLoadingStates.phone && (
              <div className="search-loading">
                <div className="simple-spinner"></div>
              </div>
            )}
            {filters.phone && !filterLoadingStates.phone && (
              <button
                type="button"
                className="clear-input-btn"
                onClick={() => onFilterClear("phone")}
                title="Clear Phone"
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
            )}
          </div>
        </td>

        <td className="filter-cell">
          <div className="floating-input-group gateway-id-input">
            <input
              type="text"
              placeholder=" "
              value={filters.gatewayId}
              onChange={(e) => onFilterValueChange("gatewayId", e.target.value)}
              className={`floating-input ${filters.gatewayId ? "has-value" : ""}`}
            />
            <label className="floating-label">Gateway ID</label>
            {filterLoadingStates.gatewayId && (
              <div className="search-loading">
                <div className="simple-spinner"></div>
              </div>
            )}
            {filters.gatewayId && !filterLoadingStates.gatewayId && (
              <button
                type="button"
                className="clear-input-btn"
                onClick={() => onFilterClear("gatewayId")}
                title="Clear Gateway ID"
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
            )}
          </div>
        </td>

        <td className="filter-cell">
          <DateRangePicker
            isOpen={isDateSubmittedOpen}
            dateRange={filters.dateSubmitted}
            onDateChange={(dateRange) => onFilterValueChange("dateSubmitted", dateRange)}
            onPresetSelect={onDatePresetSelect}
            onClear={() => onFilterClear("dateSubmitted")}
            onToggle={() => {
              setIsDateSubmittedOpen((prev) => {
                const newValue = !prev;
                if (newValue) {
                  setIsStatusDropdownOpen(false);
                }
                return newValue;
              });
            }}
            label="Submitted Date Range"
            isLoading={filterLoadingStates.dateSubmitted}
            className="submitted-date-picker"
          />
        </td>

        <td className="filter-cell">
          <div className="floating-input-group select-status">
            <div
              className={`status-dropdown ${isStatusDropdownOpen ? "open" : ""} ${
                filters.status.length > 0 ? "has-selection" : ""
              }`}
              onKeyDown={(e) => {
                if (e.key === "Escape") {
                  setIsStatusDropdownOpen(false);
                }
              }}
            >
              <div className="dropdown-header" onClick={onStatusDropdownToggle}>
                <div className="dropdown-header-content">
                  <span className="dropdown-text">
                    {filters.status.length > 0 ? filters.status.join(", ") : "Select Status"}
                  </span>
                  {filters.status.length > 0 && (
                    <span className="status-badge">{filters.status.length}</span>
                  )}
                </div>
                <svg
                  className={`dropdown-arrow ${isStatusDropdownOpen ? "open" : ""}`}
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <polyline points="6 9 12 15 18 9"></polyline>
                </svg>
              </div>
              <div className={`dropdown-content ${isStatusDropdownOpen ? "open" : ""}`}>
                <div className="status-search-wrapper">
                  <input
                    type="text"
                    className="status-search-input"
                    placeholder="Search statuses..."
                    value={statusSearchTerm}
                    onChange={(e) => setStatusSearchTerm(e.target.value)}
                    onClick={(e) => e.stopPropagation()}
                  />
                  {statusSearchTerm ? (
                    <button
                      type="button"
                      className="clear-search-btn"
                      onClick={() => setStatusSearchTerm("")}
                      title="Clear search"
                    >
                      <svg
                        width="14"
                        height="14"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <line x1="18" y1="6" x2="6" y2="18"></line>
                        <line x1="6" y1="6" x2="18" y2="18"></line>
                      </svg>
                    </button>
                  ) : (
                    <svg
                      className="search-icon"
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <circle cx="11" cy="11" r="8"></circle>
                      <path d="m21 21-4.35-4.35"></path>
                    </svg>
                  )}
                </div>

                {filters.status.length > 0 && (
                  <div className="selected-statuses-wrapper">
                    <div className="selected-statuses-header">
                      <span className="selected-statuses-label">
                        <svg
                          width="12"
                          height="12"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <polyline points="20 6 9 17 4 12"></polyline>
                        </svg>
                        Selected ({filters.status.length})
                      </span>
                      <button
                        type="button"
                        className="clear-all-btn"
                        onClick={() => onFilterClear("status")}
                        title="Clear all selections"
                      >
                        Clear All
                      </button>
                    </div>
                    <div className="selected-statuses-tags">
                      {filters.status.map((status) => (
                        <span key={status} className="status-tag">
                          {status}
                          <button
                            type="button"
                            className="remove-status-btn"
                            onClick={() => onStatusRemove(status)}
                            title={`Remove ${status}`}
                          >
                            <svg
                              width="12"
                              height="12"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            >
                              <line x1="18" y1="6" x2="6" y2="18"></line>
                              <line x1="6" y1="6" x2="18" y2="18"></line>
                            </svg>
                          </button>
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                <div className="status-divider"></div>

                <div className="checkboxes-grid">
                  <label className="select-all-label">
                    <input
                      type="checkbox"
                      checked={filters.status.length === availableStatuses.length}
                      onChange={(e) => {
                        if (e.target.checked) {
                          availableStatuses.forEach((status) => {
                            if (!filters.status.includes(status)) {
                              onStatusCheckboxChange(status, true);
                            }
                          });
                        } else {
                          onFilterClear("status");
                        }
                      }}
                    />
                    <span className="checkbox-label-text">Select All</span>
                  </label>
                  {filteredStatuses.length > 0 ? (
                    filteredStatuses.map((status) => (
                      <label key={status} className="status-checkbox-label">
                        <input
                          type="checkbox"
                          checked={filters.status.includes(status)}
                          onChange={(e) => onStatusCheckboxChange(status, e.target.checked)}
                        />
                        <span className="checkbox-label-text">{status}</span>
                      </label>
                    ))
                  ) : (
                    <div className="no-results">
                      <svg
                        width="40"
                        height="40"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <circle cx="11" cy="11" r="8"></circle>
                        <path d="m21 21-4.35-4.35"></path>
                      </svg>
                      <p>No statuses found</p>
                      <small>Try a different search term</small>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </td>
      </tr>
    );
  }, [
    filters,
    filterLoadingStates,
    isStatusDropdownOpen,
    statusSearchTerm,
    availableStatuses,
    filteredStatuses,
    onFilterValueChange,
    onFilterClear,
    onStatusCheckboxChange,
    onStatusRemove,
    onStatusDropdownToggle,
    onDatePresetSelect,
    isDateSubmittedOpen,
  ]);

  return (
    <div className="modern-dashboard">
      <section className="db-inner">
        <div className="container-fluid">
          <div className="row">
            <div className="col-12 pt-3">
              <RateReviewTable
                leads={sortedLeads}
                headers={RATE_REVIEW_CONSTANTS.TABLE_HEADERS}
                isLoading={isTableLoading}
                onLeadClick={onLeadRowClick}
                onSort={onColumnSort}
                sortColumn={sortColumn}
                sortDirection={sortDirection}
                secondarySortColumn={null}
                secondarySortDirection={null}
                rows={RATE_REVIEW_CONSTANTS.DEFAULT_PER_PAGE}
                isLoadingMore={isLoadingMore}
                hasMore={hasMore}
                onLoadMore={loadMoreLeads}
                hideFooter={false}
                footerLoadingState={isTableLoading}
                disableLoadMoreButton={false}
                canEdit={canEdit}
                filterRow={filterRow}
              />
            </div>
          </div>
        </div>
      </section>

      <UpdateLeadModal
        open={isUpdateModalVisible}
        onCancel={onUpdateModalClose}
        selectedLead={selectedLead}
        form={updateForm}
        onFinish={onLeadUpdate}
        loading={isUpdatingLead}
        saveTick={saveTick}
        canEdit={canEdit}
      />
    </div>
  );
};

export default RateReviewDashboard;
